import { Container, Graphics } from 'pixi.js';
import { TILE, type GeneratedMap } from '../map/MapGen';
import { Palette } from '../render/Palette';

const WIDTH = 150;
const REVEAL_RADIUS_TILES = 11;

/**
 * Fog-of-explored minimap (top-right). The exit marker appears once its tile
 * has been revealed — discoverable, never gated.
 */
export class Minimap {
  readonly root = new Container();
  private gfx = new Graphics();
  private explored: Uint8Array;
  private map: GeneratedMap;
  private scale: number;
  /** Marker/dot pixel sizes scale with the display width (pause blow-up). */
  private glyphScale = 1;
  private exitSeen = false;
  private shopSeen = false;
  private markerSeen: boolean[];
  /** Consumed events vanish (round 5); revealAll deliberately can't resurrect. */
  private hidden: boolean[];
  /** Summoning circle (tile coords). Not fog-gated: its formation is
   *  announced run-wide, so the map may point the way. */
  private circle: { x: number; y: number } | null = null;
  /** Gaunt's body (tile coords) while he's down. */
  private body: { x: number; y: number } | null = null;
  private dirty = true;
  private sinceRedraw = 0;

  constructor(
    map: GeneratedMap,
    private readonly hasShop: boolean,
    /** Extra points of interest (events) in tile coords; drawn once revealed —
     *  unless `always` (the vault door: rare + high-value, and chunk-floor
     *  pockets made exact-tile discovery unreliable; playtest 2026-07-23). */
    private readonly markers: { x: number; y: number; color: number; always?: boolean }[] = [],
    /** Exit marker visible from floor start (Wick: the candle clock turns
     *  "where is the exit" into a survival question — he gets to plan). */
    exitAlways = false,
  ) {
    this.map = map;
    this.explored = new Uint8Array(map.w * map.h);
    this.scale = WIDTH / map.w;
    this.markerSeen = markers.map((m) => m.always ?? false);
    this.hidden = markers.map(() => false);
    this.exitSeen = exitAlways;
    this.root.addChild(this.gfx);
    this.root.alpha = 0.85;
  }

  /** Rendered height at the current width (maps aren't square). */
  get displayHeight(): number {
    return this.map.h * this.scale;
  }

  /** Resize the display (pause screen blow-up); glyphs scale along. */
  setWidth(px: number): void {
    this.scale = px / this.map.w;
    this.glyphScale = px / WIDTH;
    this.dirty = true;
    this.sinceRedraw = 15; // beat the redraw throttle — resize shows NOW
  }

  /** A consumed event's marker vanishes for good (idempotent). */
  hideMarker(k: number): void {
    if (k < 0 || k >= this.hidden.length || this.hidden[k]) return;
    this.hidden[k] = true;
    this.dirty = true;
  }

  /** Mark (or clear) the summoning circle, in tile coords. */
  setCircle(pos: { x: number; y: number } | null): void {
    this.circle = pos;
    this.dirty = true;
  }

  /** Mark (or clear) Gaunt's body, in tile coords — the revive target is
   *  announced by its toast, so it isn't fog-gated (same rule as the circle). */
  setBody(pos: { x: number; y: number } | null): void {
    this.body = pos;
    this.dirty = true;
  }

  /**
   * Cartographer's Sketch (shop, 2026-07-27): every marker, the exit, and
   * the shop light up at once. Fog over the tiles stays — the sketch sells
   * WHAT and WHERE, walking still draws the map.
   */
  revealAll(): void {
    this.markerSeen = this.markerSeen.map(() => true);
    this.exitSeen = true;
    if (this.hasShop) this.shopSeen = true;
    this.dirty = true;
  }

  /** Call each sim tick with the player's world position. */
  reveal(worldX: number, worldY: number): void {
    const ptx = Math.floor(worldX / TILE);
    const pty = Math.floor(worldY / TILE);
    const r = REVEAL_RADIUS_TILES;
    const m = this.map;
    for (let ty = Math.max(0, pty - r); ty <= Math.min(m.h - 1, pty + r); ty++) {
      for (let tx = Math.max(0, ptx - r); tx <= Math.min(m.w - 1, ptx + r); tx++) {
        const i = ty * m.w + tx;
        if (this.explored[i] === 0 && (tx - ptx) ** 2 + (ty - pty) ** 2 <= r * r) {
          this.explored[i] = 1;
          this.dirty = true;
          if (tx === m.exit.x && ty === m.exit.y) this.exitSeen = true;
          if (this.hasShop && tx === m.shop.x && ty === m.shop.y) this.shopSeen = true;
          for (let k = 0; k < this.markers.length; k++) {
            const mk = this.markers[k]!;
            if (tx === mk.x && ty === mk.y) this.markerSeen[k] = true;
          }
        }
      }
    }
  }

  /** Call each render frame; the fog redraws at most every 15 frames. */
  sync(playerX: number, playerY: number): void {
    this.drawPlayerDot(playerX, playerY);
    if (++this.sinceRedraw < 15 || !this.dirty) return;
    this.sinceRedraw = 0;
    this.dirty = false;

    const g = this.gfx;
    const s = this.scale;
    const m = this.map;
    g.clear();
    g.rect(0, 0, m.w * s, m.h * s).fill({ color: Palette.hudPanel, alpha: 0.75 });
    for (let ty = 0; ty < m.h; ty++) {
      for (let tx = 0; tx < m.w; tx++) {
        const i = ty * m.w + tx;
        if (this.explored[i] === 1 && m.tiles[i] === 1) {
          g.rect(tx * s, ty * s, s, s).fill({ color: Palette.hudDim, alpha: 0.55 });
        }
      }
    }
    const gs = this.glyphScale;
    if (this.exitSeen) {
      g.circle(m.exit.x * s, m.exit.y * s, 3 * gs).fill(Palette.gold);
    }
    if (this.shopSeen) {
      // Square marker: the shop stays findable for walk-back prep.
      g.rect(m.shop.x * s - 2.5 * gs, m.shop.y * s - 2.5 * gs, 5 * gs, 5 * gs).fill(Palette.gem);
    }
    for (let k = 0; k < this.markers.length; k++) {
      if (!this.markerSeen[k] || this.hidden[k]) continue;
      const mk = this.markers[k]!;
      const mx = mk.x * s;
      const my = mk.y * s;
      const r = 3 * gs;
      g.poly([mx, my - r, mx + r, my, mx, my + r, mx - r, my]).fill(mk.color);
    }
    if (this.circle) {
      const cx = this.circle.x * s;
      const cy = this.circle.y * s;
      g.circle(cx, cy, 4 * gs).stroke({ width: 1.5 * gs, color: Palette.enemyHot, alpha: 0.95 });
      g.circle(cx, cy, 1.5 * gs).fill(Palette.enemyHot);
    }
    if (this.body) {
      const bx = this.body.x * s;
      const by = this.body.y * s;
      const r = 3.5 * gs;
      g.poly([bx, by - r, bx + r, by, bx, by + r, bx - r, by])
        .stroke({ width: 1.5 * gs, color: Palette.playerCool, alpha: 0.95 });
    }
  }

  /** Separate tiny graphic so movement never re-rasterizes the fog. */
  private drawPlayerDot(playerX: number, playerY: number): void {
    this.playerDot.clear();
    this.playerDot
      .circle((playerX / TILE) * this.scale, (playerY / TILE) * this.scale, 2.5 * this.glyphScale)
      .fill(Palette.playerCool);
    if (!this.playerDot.parent) this.root.addChild(this.playerDot);
  }

  private playerDot = new Graphics();
}
